import * as XLSX from "xlsx-js-style";

type ExcelCellValue = string | number | boolean | Date | null | undefined;

type ExcelRowStyle = {
  fillColor?: string;
  fontColor?: string;
  bold?: boolean;
};

type ExcelExportRow =
  | ExcelCellValue[]
  | {
      values: ExcelCellValue[];
      style?: ExcelRowStyle;
    };

type ExportExcelOptions = {
  fileName: string;
  sheetName: string;
  rows: ExcelExportRow[];
};

const sanitizeSheetName = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return "Sayfa1";
  }

  const invalidSheetChars = new Set([":", "\\", "/", "?", "*", "[", "]"]);
  return (
    Array.from(trimmed)
      .map((char) => (invalidSheetChars.has(char) ? " " : char))
      .join("")
      .slice(0, 31) || "Sayfa1"
  );
};

const sanitizeFileName = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return "export.xlsx";
  }

  const normalized = trimmed.replace(/[<>:"/\\|?*]+/g, "_");
  return normalized.toLowerCase().endsWith(".xlsx")
    ? normalized
    : `${normalized}.xlsx`;
};

const getColumnWidth = (value: ExcelCellValue): number => {
  if (value === null || value === undefined) {
    return 10;
  }

  if (value instanceof Date) {
    return 16;
  }

  if (typeof value === "number") {
    return Math.max(String(value).length + 2, 10);
  }

  const length = String(value).replace(/\r?\n/g, " ").length;
  return Math.min(Math.max(length + 2, 10), 40);
};

export const exportRowsToExcel = ({
  fileName,
  sheetName,
  rows,
}: ExportExcelOptions): void => {
  const normalizedRows = rows.map((row) =>
    Array.isArray(row) ? { values: row, style: undefined } : row,
  );
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.aoa_to_sheet(
    normalizedRows.map((row) => row.values),
  );
  const maxColumnCount = normalizedRows.reduce(
    (max, row) => Math.max(max, row.values.length),
    0,
  );

  worksheet["!cols"] = Array.from({ length: maxColumnCount }, (_, colIndex) => {
    const width = normalizedRows.reduce((max, row) => {
      return Math.max(max, getColumnWidth(row.values[colIndex]));
    }, 10);

    return { wch: width };
  });

  normalizedRows.forEach((row, rowIndex) => {
    const rowStyle = row.style;
    if (!rowStyle) {
      return;
    }

    const fillColor = rowStyle.fillColor?.replace(/^#/, "").toUpperCase();
    const fontColor = rowStyle.fontColor?.replace(/^#/, "").toUpperCase();

    row.values.forEach((_, columnIndex) => {
      const cellRef = XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex });
      const cell = worksheet[cellRef];

      if (!cell) {
        return;
      }

      cell.s = {
        ...(fillColor
          ? {
              fill: {
                patternType: "solid",
                fgColor: { rgb: fillColor },
              },
            }
          : {}),
        ...((fontColor || rowStyle.bold)
          ? {
              font: {
                ...(fontColor ? { color: { rgb: fontColor } } : {}),
                ...(rowStyle.bold ? { bold: true } : {}),
              },
            }
          : {}),
      };
    });
  });

  if (worksheet["!ref"]) {
    worksheet["!autofilter"] = { ref: worksheet["!ref"] };
  }

  XLSX.utils.book_append_sheet(
    workbook,
    worksheet,
    sanitizeSheetName(sheetName),
  );
  XLSX.writeFile(workbook, sanitizeFileName(fileName));
};
